import useTheme from '@cvent/blocks/hooks/useTheme';
import { useTranslate } from 'nucleus-text';
import Button, { CarinaButtonProps } from '@cvent/carina/components/Button';
import { ExternalLinkIcon } from '@components/shared/V1ToV2Icon';
import useRegLink from '@hooks/useRegLink';

interface FeeProps {
  currency: string;
  amount: number;
  locale: string;
  isSmall?: boolean;
}
interface RegProps {
  isRegModEnabled: boolean;
  onClick: () => void;
  size?: CarinaButtonProps['size'];
}

/** Fee text for a session
 * - Should rely on showSessionFee from tools/getFeeAndCapacityInfo for visibility
 */
export function SessionFee({ currency = 'USD', amount, locale, isSmall = true }: FeeProps): JSX.Element {
  const { font } = useTheme();
  const { translate } = useTranslate();

  const style = isSmall
    ? { color: font.color.neutral.base }
    : {
        color: font.color.soft,
        fontWeight: font.base.weight.bold
      };

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    // trailingZeroDisplay does not yet exist for NumberFormat options in Typescript:
    // https://github.com/microsoft/TypeScript/issues/56269
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    trailingZeroDisplay: 'stripIfInteger'
  });

  return (
    <span css={style} aria-label={translate('Sessions-Cost-AriaLabel')}>
      {formatter.format(amount)}
    </span>
  );
}

/** Purchase Session button
 * - Should rely on showSessionFee from tools/getFeeAndCapacityInfo for visibility
 */
export function SessionFeeRegButton({ isRegModEnabled, onClick, size = 'l' }: RegProps): JSX.Element {
  const { translate } = useTranslate();
  const registrationLink = useRegLink();

  return isRegModEnabled ? (
    <Button<'a'>
      icon={ExternalLinkIcon}
      text={translate('Sessions-Purchase-Registration')}
      appearance="filled"
      size={size}
      element="a"
      href={registrationLink}
      target="_blank"
    />
  ) : (
    <Button text={translate('Sessions-Purchase-Registration')} appearance="filled" size={size} onClick={onClick} />
  );
}
